// Summoned creatures (core's summons.ts) on screen: magpies, the clay golem, wolves. Magpies are drawn birds built
// from primitives (birdFigures.ts, light enough for a flock), the golem is a Blender glTF rig with its own clips
// (art/heroes/summoner/golem), wolves are ink-stroke demons (demonWolves.ts). Each has a team-coloured mark and a
// small hp bar turned to the camera. Driven every frame by GameView.summons, which already holds only the ones the
// viewer may see; a figure whose summon leaves that list is removed — a golem that fell apart (a `summonGone` fx
// seen for it) crumbles first.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { adoptMesh, mk, release, TEAM } from './engrave.js';
import { buildInkWolf, type InkWolf } from './demonWolves.js';
import { buildMagpie as magpieBody } from './birdFigures.js';
import { loadModel } from './heroView.js';
import type { SummonKind } from '../core/summons.js';
import type { Team } from '../core/layout.js';
import type { GameView, ViewSummon } from './gameView.js';

/** View-only per-kind dressing: hover/target ring radius, hp bar height, the heights input.ts probes a click at. */
export const SUMMON_LOOK: Record<SummonKind, { ring: number; barY: number; pickY: number[] }> = {
  magpie: { ring: 0.55, barY: 2.9, pickY: [1.9, 2.3] },
  golem: { ring: 1.05, barY: 2.6, pickY: [0.6, 1.4, 2.0] },
  wolf: { ring: 0.8, barY: 1.4, pickY: [0.5, 0.85] },
};

export const GOLEM_MODEL = 'assets/summon_golem.gltf.json';
const GOLEM_LOOPING = new Set(['Idle', 'Walk']);
const CRUMBLE = 1.4;       // s the golem's figure outlives its summon: the Death clip settling into a heap…
const SETTLED = 1.1;       // …then sinking out of sight
const GONE_WINDOW = 1.0;   // s a `summonGone` waits for its golem to leave the list (online it surfaces first)
const BAR_W = 0.9, BAR_H = 0.09;
const SNAP = 4;  // m: past this the figure jumps to the summon instead of gliding (a first sight, a teleport)

type Figure = {
  id: number; kind: SummonKind; root: THREE.Group; body: THREE.Group;
  wings: THREE.Object3D[]; bar: THREE.Group; fill: THREE.Mesh; phase: number;
  wolf: InkWolf | null; clock: number;  // clock: seconds since the figure appeared, the wolf's animation time
  // the golem's glTF rig, once loaded: its geometry belongs to heroView's model cache
  model: THREE.Group | null; mixer: THREE.AnimationMixer | null; actions: Record<string, THREE.AnimationAction> | null;
  current: THREE.AnimationAction | null; lastSwingAgo: number;
  /** Seconds since the golem started crumbling, null while it stands. */
  dying: number | null;
  /** Set once removed: a model still loading must not register its meshes with the engraver. */
  disposed: boolean;
};

const loader = new GLTFLoader();

function buildMagpie(f: Figure, team: Team) {
  f.wings.push(...magpieBody(f.body));
  const mark = mk(new THREE.BoxGeometry(0.2, 0.03, 0.12), TEAM[team], { team });  // a team band across the back
  mark.position.set(0, 0.15, 0.12);
  f.body.add(mark);
  f.body.position.y = 2.1;
}

function buildGolem(f: Figure, team: Team) {
  loadModel(loader, GOLEM_MODEL, (gltf) => {
    if (f.disposed) return;
    const model = gltf.scene as THREE.Group;
    model.traverse((o: THREE.Object3D) => { if ((o as THREE.Mesh).isMesh) adoptMesh(o as THREE.Mesh, team); });
    const mixer = new THREE.AnimationMixer(model), actions: Record<string, THREE.AnimationAction> = {};
    for (const clip of gltf.animations as THREE.AnimationClip[]) {
      const a = mixer.clipAction(clip);
      if (!GOLEM_LOOPING.has(clip.name)) { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
      actions[clip.name] = a;
    }
    f.body.add(model);
    Object.assign(f, { model, mixer, actions, current: null });
    golemClip(f, f.dying !== null ? 'Death' : 'Idle', 0, true);
  }, (err: any) => console.warn('Golem model failed to load:', GOLEM_MODEL, err && err.message || err));
}

function golemClip(f: Figure, name: string, fade: number, restart = false) {
  const next = f.actions?.[name];
  if (!next || (f.current === next && !restart)) return;
  const prev = f.current;
  next.reset().setEffectiveWeight(1);
  if (prev !== next && fade > 0) next.fadeIn(fade);
  next.play();
  if (prev && prev !== next) { if (fade > 0) prev.fadeOut(fade); else prev.stop(); }
  f.current = next;
}

/* A fresh swing (its clock went back to near zero) restarts the slam; otherwise the slam plays out, then walk or stand. */
function driveGolem(f: Figure, s: ViewSummon, dt: number) {
  if (s.swingAgo < f.lastSwingAgo && s.swingAgo < 0.3) golemClip(f, 'Attack', 0.08, true);
  f.lastSwingAgo = s.swingAgo;
  const attack = f.actions?.Attack;
  if (!(attack && f.current === attack && attack.isRunning())) golemClip(f, s.moving ? 'Walk' : 'Idle', 0.2);
  f.mixer?.update(dt);
}

function buildWolf(f: Figure, team: Team) {
  f.wolf = buildInkWolf(team, f.id);
  f.body.add(f.wolf.body);
}

function createFigure(scene: THREE.Scene, s: ViewSummon): Figure {
  const root = new THREE.Group(), body = new THREE.Group(), bar = new THREE.Group();
  root.add(body);
  const f: Figure = { id: s.id, kind: s.kind, root, body, wings: [], bar, fill: null as unknown as THREE.Mesh, phase: (s.id * 1.7) % (Math.PI * 2),
                      wolf: null, clock: 0, model: null, mixer: null, actions: null, current: null, lastSwingAgo: s.swingAgo, dying: null,
                      disposed: false };
  if (s.kind === 'magpie') buildMagpie(f, s.team);
  else if (s.kind === 'golem') buildGolem(f, s.team);
  else buildWolf(f, s.team);
  const back = mk(new THREE.PlaneGeometry(BAR_W + 0.06, BAR_H + 0.06), 0x2c1e12, { fx: true, cast: false });
  const fill = mk(new THREE.PlaneGeometry(BAR_W, BAR_H), TEAM[s.team], { fx: true, cast: false });
  fill.position.z = 0.01;
  bar.add(back, fill);
  bar.position.y = SUMMON_LOOK[s.kind].barY;
  root.add(bar);
  f.fill = fill;
  root.position.copy(s.pos);
  root.rotation.y = s.yaw;
  scene.add(root);
  return f;
}

const toCamera = new THREE.Vector3();
const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));

type PendingGone = { x: number; z: number; age: number };
export type SummonViews = Map<number, Figure> & { gone: PendingGone[] };
export function createSummonViews(): SummonViews { return Object.assign(new Map<number, Figure>(), { gone: [] as PendingGone[] }); }

/** One frame: create figures for new summons, glide and animate the rest, drop the ones gone from the view (a golem
    that fell apart crumbles first). Reads this frame's events: call before GameView.clearEvents. */
export function driveSummons(figs: SummonViews, scene: THREE.Scene, view: GameView, camera: THREE.Camera, dt: number) {
  const gone = figs.gone;
  for (const g of gone) g.age += dt;
  for (let i = gone.length - 1; i >= 0; i--) if (gone[i]!.age > GONE_WINDOW) gone.splice(i, 1);
  for (const e of view.events) if (e.type === 'fx' && e.name === 'summonGone' && e.kind === 'golem') gone.push({ x: e.pos.x, z: e.pos.z, age: 0 });

  const seen = view.summons, live = new Set<number>();
  for (const s of seen) {
    live.add(s.id);
    let f = figs.get(s.id);
    if (!f) { f = createFigure(scene, s); figs.set(s.id, f); }
    const k = 1 - Math.exp(-dt * 14);
    if (f.root.position.distanceTo(s.pos) > SNAP) f.root.position.copy(s.pos);
    else f.root.position.lerp(s.pos, k);
    f.root.rotation.y += angDiff(f.root.rotation.y, s.yaw) * k;
    f.phase += dt * (s.kind === 'magpie' ? 16 : s.moving ? 10 : 2);
    // A fresh attack reads as a lunge of the whole body (the golem's own clip carries its slam).
    const lunge = s.swingAgo < 0.3 ? Math.sin((s.swingAgo / 0.3) * Math.PI) : 0;
    if (s.kind === 'magpie') {
      f.body.position.z = lunge * 0.35;
      f.body.position.y = 2.1 + Math.sin(f.phase * 0.25) * 0.12 - lunge * 0.8;
      f.body.rotation.x = lunge * 0.7;  // the peck: beak down
      for (const w of f.wings) w.rotation.z = (w.userData.side as number) * Math.sin(f.phase) * 0.7;
    } else if (s.kind === 'golem') {
      driveGolem(f, s, dt);
    } else if (f.wolf) {
      f.body.position.z = lunge * 0.35;
      f.clock += dt;
      f.wolf.animate(f.clock, s.moving, lunge, toCamera.subVectors(camera.position, f.root.position).normalize());
    }
    // The last second of its life it fades toward the page: the figure sinks (a golem crumbles instead).
    if (s.kind !== 'golem') f.body.scale.setScalar(0.6 + 0.4 * Math.min(1, s.left / 1));
    const hp = Math.max(0, Math.min(1, s.maxHp > 0 ? s.hp / s.maxHp : 0));
    f.fill.scale.x = Math.max(0.001, hp);
    f.fill.position.x = -BAR_W * (1 - hp) / 2;
    f.bar.quaternion.copy(camera.quaternion);
    f.bar.quaternion.premultiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -f.root.rotation.y, 0)));
  }
  for (const [id, f] of figs) {
    if (live.has(id)) continue;
    if (f.dying === null && f.kind === 'golem' && takeGone(gone, f)) {
      f.dying = 0;
      f.bar.visible = false;
      golemClip(f, 'Death', 0.1, true);
    }
    if (f.dying !== null && f.dying < CRUMBLE) {
      f.dying += dt;
      f.root.position.y = -0.6 * Math.max(0, (f.dying - SETTLED) / (CRUMBLE - SETTLED));  // the heap sinks into the page
      f.mixer?.update(dt);
      continue;
    }
    removeFigure(f, scene);
    figs.delete(id);
  }
}

function takeGone(gone: PendingGone[], f: Figure): boolean {
  const i = gone.findIndex(g => Math.hypot(f.root.position.x - g.x, f.root.position.z - g.z) < 2);
  if (i < 0) return false;
  gone.splice(i, 1);
  return true;
}

function removeFigure(f: Figure, scene: THREE.Scene) {
  f.disposed = true;
  scene.remove(f.root);
  f.wolf?.dispose();
  f.mixer?.stopAllAction();
  if (f.model) { f.model.removeFromParent(); release(f.model, { geometry: false }); }  // geometry belongs to heroView's model cache
  release(f.root);
}

export function disposeSummonViews(figs: SummonViews, scene: THREE.Scene) {
  for (const f of figs.values()) removeFigure(f, scene);
  figs.clear();
  figs.gone.length = 0;
}
